import { useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { dateTime } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input } from '@/components/ui/form'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { PortalUserListItem } from '@/types/api'

/**
 * Who at a partner may sign in to the reseller portal.
 *
 * <p>
 * The only place a portal account is created. Inviting asks ARMS to make a passwordless
 * account first and saves the row second: ARMS emails the activation link and owns the
 * password, QuoteHero never sees a credential, and a provisioning failure saves nothing -
 * a user row nobody was told about is the state this replaced.
 * </p>
 * <p>
 * One address holds a staff account or a portal account, never both. Inviting a Codemax
 * address here is refused, and inviting a partner address on the Users screen is refused
 * too.
 * </p>
 */
export function ResellerPortalUsers({ resellerId }: { resellerId: string }) {
  const { can } = useAuth()
  const [inviting, setInviting] = useState(false)
  const [busy, setBusy] = useState<string | undefined>()
  const toast = useToast()

  const users = useAsync<PortalUserListItem[]>(
    (signal) =>
      api.get<PortalUserListItem[]>(`/resellers/${resellerId}/portal-users`, undefined, signal),
    [resellerId],
  )

  if (users.loading && !users.data) return <Loading />
  if (users.error) {
    return <ErrorState title={users.error.title} detail={users.error.detail} onRetry={users.reload} />
  }

  const manageable = can('reseller.portal.manage')

  async function act(user: PortalUserListItem, action: 'resend-invitation' | 'deactivate' | 'reactivate') {
    setBusy(user.id)
    try {
      await api.post(`/resellers/${resellerId}/portal-users/${user.id}/${action}`)
      toast.show(
        action === 'resend-invitation'
          ? 'Invitation sent again'
          : action === 'deactivate'
            ? 'Portal access withdrawn'
            : 'Portal access restored',
        'success',
      )
      users.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not do that', 'error')
    } finally {
      setBusy(undefined)
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title="People who can sign in"
          hint="Codemax ARMS owns their password and emails the activation link. QuoteHero never sees a credential."
          action={
            manageable ? (
              <Button variant="primary" onClick={() => setInviting(true)}>
                Invite somebody
              </Button>
            ) : undefined
          }
        />

        {(users.data ?? []).length === 0 ? (
          <EmptyState
            title="Nobody has portal access"
            hint={
              manageable
                ? 'Invite the person at this partner who reconciles their commission.'
                : 'Inviting a partner sits with the Director of Sales and the Owner.'
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Status</Th>
                <Th>Last signed in</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((user) => (
                <Tr key={user.id}>
                  <Td>{user.displayName}</Td>
                  <Td className="font-mono text-[12px]">{user.email}</Td>
                  <Td>
                    {!user.isActive ? (
                      <Badge>Withdrawn</Badge>
                    ) : user.pendingActivation ? (
                      <Badge tone="warning">Invited</Badge>
                    ) : (
                      <Badge tone="positive">Active</Badge>
                    )}
                  </Td>
                  <Td>
                    {user.lastLoginAt ? (
                      dateTime(user.lastLoginAt)
                    ) : (
                      <span className="text-faint">&mdash;</span>
                    )}
                  </Td>
                  <Td className="text-right">
                    {manageable && (
                      <div className="flex justify-end gap-2">
                        {user.isActive && user.pendingActivation && (
                          <Button
                            disabled={busy === user.id}
                            onClick={() => act(user, 'resend-invitation')}
                          >
                            Resend invitation
                          </Button>
                        )}
                        {user.isActive ? (
                          <Button disabled={busy === user.id} onClick={() => act(user, 'deactivate')}>
                            Deactivate
                          </Button>
                        ) : (
                          <Button disabled={busy === user.id} onClick={() => act(user, 'reactivate')}>
                            Reactivate
                          </Button>
                        )}
                      </div>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <InvitePortalUser
        open={inviting}
        resellerId={resellerId}
        onClose={() => setInviting(false)}
        onDone={() => {
          setInviting(false)
          users.reload()
        }}
      />
    </>
  )
}

function InvitePortalUser({
  open,
  resellerId,
  onClose,
  onDone,
}: {
  open: boolean
  resellerId: string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [saving, setSaving] = useState(false)

  async function invite() {
    setSaving(true)
    try {
      await api.post(`/resellers/${resellerId}/portal-users`, {
        email: email.trim(),
        displayName: displayName.trim(),
      })
      toast.show('Invitation sent', 'success')
      // Emptied on the way out, because the dialog is mounted once and reopened rather
      // than remounted. Left filled, the next invitation opens holding the person who
      // was just invited, and the obvious thing to do with a form that is already
      // complete is press the button - which is a second invitation to the same address.
      setEmail('')
      setDisplayName('')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not invite them', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Invite somebody to the portal"
      description="They will be able to see this partner's signed deals, their commission statements and their payouts."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={invite}
            disabled={saving || email.trim() === '' || displayName.trim() === ''}
          >
            {saving ? 'Inviting...' : 'Invite'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Email"
          required
          hint="A Codemax address is refused: one address holds a staff account or a portal account, never both."
        >
          {(id) => (
            <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <Field label="Name" required>
          {(id) => (
            <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
